import { ShopigurusHTMLElement } from '@shopigurus/base';

/**
 * ShpfltReviewsCarousel — host element for the reviews carousel.
 *
 * The carousel itself is driven by the theme's Swiper integration (triggered
 * by [data-swiper-slider] inside this element). This component only owns the
 * poster→<video> swap: video tags stay as inert <template> elements until the
 * user taps a poster, so no video bytes load on initial render.
 *
 * @example
 * <shpflt-reviews-carousel data-section-id="{{ section.id }}">
 *   <div class="swiper" data-swiper-slider>...</div>
 * </shpflt-reviews-carousel>
 */
class ShpfltReviewsCarousel extends ShopigurusHTMLElement {
  /** @type {ResizeObserver|null} */
  #resizeObserver = null;
  /** @type {MutationObserver|null} */
  #activeSlideObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handlePosterClick, {
      signal: this.abortController.signal,
    });

    // Measure tallest detail card and reserve exact space for it. Re-measures
    // when the viewport changes width (slide width and detail text wrapping
    // both change with breakpoints).
    this.#measureDetailHeight();
    this.#resizeObserver = new ResizeObserver(() => this.#measureDetailHeight());
    this.#resizeObserver.observe(this);

    // Preload video metadata for the active slide so play-on-tap is instant.
    // Watches Swiper's swiper-slide-active class toggles and injects/removes
    // a hidden <video preload="metadata"> as slides scroll past.
    this.#installActiveSlideObserver();

    // The theme's data-swiper-slider auto-init runs once on initial page load.
    // When Shopify replaces the section's DOM (settings edited in the editor,
    // or section:load fires), the new .swiper has no Swiper instance attached.
    // Re-bootstrap it by importing the same chunk the theme already loaded.
    queueMicrotask(() => this.#ensureSwiperInitialized());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#activeSlideObserver?.disconnect();
    this.#activeSlideObserver = null;
  }

  /**
   * Observe each slide's class attribute. When a slide gains
   * `swiper-slide-active`, inject a hidden <video preload="metadata"> so the
   * browser fetches the first few KB (header/index) and play-on-tap is
   * instant. When it loses the class, remove the warming video to free
   * bandwidth and memory.
   */
  #installActiveSlideObserver() {
    const slides = this.querySelectorAll('.swiper-slide');
    if (!slides.length) return;

    this.#activeSlideObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName !== 'class') continue;
        const slide = /** @type {HTMLElement} */ (m.target);
        if (slide.classList.contains('swiper-slide-active')) {
          this.#warmActiveSlide(slide);
        } else {
          this.#unwarmSlide(slide);
        }
      }
    });

    for (const slide of slides) {
      this.#activeSlideObserver.observe(slide, {
        attributes: true,
        attributeFilter: ['class'],
      });
      // Handle the slide that's already active when we connect.
      if (slide.classList.contains('swiper-slide-active')) {
        this.#warmActiveSlide(slide);
      }
    }
  }

  /**
   * Inject a hidden, preload=metadata video into the slide if it has a video
   * template and isn't already warmed. The actual playable video is still
   * created from the template on user tap — by then the browser has cached
   * enough bytes to start playback instantly.
   * @param {HTMLElement} slide
   */
  #warmActiveSlide(slide) {
    const template = slide.querySelector(
      'template.shpflt-reviews-carousel__video-template'
    );
    if (!template) return;
    if (slide.querySelector('video[data-warm-preload]')) return;

    const fragment = template.content.cloneNode(true);
    const video = fragment.querySelector('video');
    if (!video) return;

    video.removeAttribute('autoplay');
    video.removeAttribute('controls');
    video.muted = true;
    video.preload = 'metadata';
    video.setAttribute('data-warm-preload', '');
    video.setAttribute('aria-hidden', 'true');
    video.setAttribute('tabindex', '-1');
    video.style.cssText =
      'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;';

    slide.appendChild(video);
  }

  /**
   * @param {HTMLElement} slide
   */
  #unwarmSlide(slide) {
    const warm = slide.querySelector('video[data-warm-preload]');
    if (warm) {
      warm.removeAttribute('src');
      warm.load();
      warm.remove();
    }
  }

  /**
   * Measure the tallest review-detail card and reserve exactly that much
   * vertical space below the slides via the --shpflt-rc-detail-reserved
   * CSS variable. Makes the layout work for arbitrarily long reviews without
   * a hardcoded magic number — short reviews collapse, long reviews don't get
   * clipped by the section's bottom edge.
   */
  #measureDetailHeight() {
    const cards = this.querySelectorAll(
      '.shpflt-reviews-carousel__detail:not(.shpflt-reviews-carousel__detail--overlay)'
    );
    if (!cards.length) {
      this.style.removeProperty('--shpflt-rc-detail-reserved');
      return;
    }
    let max = 0;
    for (const card of cards) {
      // offsetHeight ignores `visibility: hidden` / `opacity: 0` — both keep
      // their geometry — so we get accurate measurements regardless of which
      // slide is active.
      max = Math.max(max, card.offsetHeight);
    }
    if (max > 0) {
      // +24px = top gap (12px) + a little breathing room at the section bottom.
      this.style.setProperty('--shpflt-rc-detail-reserved', `${max + 24}px`);
    }
  }

  /**
   * If the inner .swiper element has no Swiper instance attached, dynamically
   * import the swiper-slider chunk (already cached by the browser since the
   * theme imported it on page load) and instantiate it.
   */
  async #ensureSwiperInitialized() {
    const swiperEl = this.querySelector('.swiper[data-swiper-slider]');
    if (!swiperEl || swiperEl.swiper) return;

    // Find the chunk URL from the resource timing log — the theme loaded it
    // on initial page parse, so it's guaranteed to be in the cache.
    const entry = performance
      .getEntriesByType('resource')
      .find((e) => /chunk--Swiper-[A-Z0-9]+\.[^/]+\.js$/.test(e.name));
    if (!entry) return;

    try {
      const mod = await import(/* @vite-ignore */ entry.name);
      if (swiperEl.swiper) return; // raced with another reinit, bail
      const instance = new mod.default(swiperEl);
      instance.init();
    } catch (_err) {
      // Swallow — if dynamic init fails the carousel still works as a static
      // scroll container due to the CSS fallback.
    }
  }

  onSectionSelect() {
    this.querySelectorAll('video').forEach((v) => v.pause());
  }

  /**
   * Theme editor: when a slide block is selected in the editor sidebar,
   * slide the carousel to that block so the merchant can see it.
   * @param {CustomEvent} e
   * @param {Object} e.detail
   * @param {string} e.detail.blockId
   */
  onBlockSelect(e) {
    const blockId = e.detail?.blockId;
    if (!blockId) return;

    const swiperEl = this.querySelector('.swiper');
    const swiper = swiperEl?.swiper;
    if (!swiper) return;

    const slide = this.querySelector(
      `.swiper-slide[data-block-id="${blockId}"]:not(.swiper-slide-duplicate)`
    );
    if (!slide) return;

    const realIndex = slide.getAttribute('data-swiper-slide-index');
    if (realIndex !== null && typeof swiper.slideToLoop === 'function') {
      swiper.slideToLoop(parseInt(realIndex, 10), 400);
    } else {
      // Non-loop fallback.
      const idx = Array.from(swiperEl.querySelectorAll('.swiper-slide')).indexOf(slide);
      if (idx >= 0) swiper.slideTo(idx, 400);
    }
  }

  /**
   * Swap a poster <button> for a real <video> on user tap.
   * Clones the inert <template> Liquid rendered — guarantees source URLs match
   * exactly what Shopify served (no JSON serialization round-trip).
   * @param {MouseEvent} e
   */
  #handlePosterClick = (e) => {
    const poster = e.target.closest('.shpflt-reviews-carousel__poster');
    if (!poster) return;
    e.preventDefault();

    const template = poster.querySelector(
      'template.shpflt-reviews-carousel__video-template'
    );
    if (!template) return;

    const fragment = template.content.cloneNode(true);
    const video = fragment.querySelector('video');
    if (!video) return;

    const img = poster.querySelector('img');
    if (img) {
      video.poster = img.currentSrc || img.src;
    }

    // The slide may already contain a hidden <video preload="metadata"> that
    // we injected when this slide went active. The real <video> reuses the
    // browser's HTTP cache so playback is near-instant; the warm one has
    // served its purpose and should be removed to avoid duplicates in the DOM.
    const slide = poster.closest('.swiper-slide');
    slide?.querySelector('video[data-warm-preload]')?.remove();

    poster.replaceWith(video);
    video.play().catch(() => {});
  };
}

customElements.define('shpflt-reviews-carousel', ShpfltReviewsCarousel);
